import React, {useCallback, useMemo} from 'react'
import {compose} from 'redux'
import {Grid, Typography, withStyles} from '@material-ui/core'
import {ETAPE_TYPE} from '../../../../../../utils/constants'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import Title from '../../layout/Title'
import {SP_BLOCK_ACTION_TYPE, SP_CROPS_TABLE_NAME} from '../tool/suiviPepiniere.constants'
import Button from '../../../../../../components/Button'
import ButtonWithMenu from '../../../../../../components/ButtonWithMenu'
import AddCommentIcon from '@material-ui/icons/AddComment'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import NoResultPage from '../../../../../../components/NoResultPage'
import SpringDataTableWithLoader from '../../../../../../components/layout/SpringDataTableWithLoader'
import {useTourPlaineContext} from '../../../TourPlaineContext'
import {toI18N} from '../../../../../../utils/utils'
import {DateFormat} from '../../../../../../utils/dateConstants'
import moment from 'moment'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'
import {injectReminderForm} from '../../../service/reminderFormInjector'
import { ACTIONS_KEY, CROP_DATATABLE_KEYS, EMPTY_PLACEHOLDER, ETAPE_CROISSANCE, TOUR_PLAINE_TABS } from '../../../tool/tourPlaine.constants'
import {injectActionsForm} from '../../../service/actionsFormInjector'
import {IN_NURSERY_CROPS_URI} from '../service/suiviPepiniereApi'
import {getPreferences} from '../../../../../common/user/services/userSelectors'
import {connect} from 'react-redux'
import {dataTableActions} from 'isotope-client'
import {inProgressCropsToApp} from '../../../tool/tourPlaine.mapper'
import classnames from 'classnames'
import {contentStyles} from '../tool/suiviPepiniere.styles'
import {formatDateToWeek} from '../../../tool/tourPlaine.utils'

const SORTABLE_KEYS = [CROP_DATATABLE_KEYS.CROP, CROP_DATATABLE_KEYS.PLANNED_BOARD]

/**
 * CropsContent for SuiviPepiniere
 * @returns {JSX.Element}
 */
const CropsContent = ({ classes, openReminderForm, getActions, idFerme, selectedRows, refreshTable, resetSelectedRows }) => {
	const { formatMessage } = useIntl()
	const { currentWeek, currentCampagneInProgress } = useTourPlaineContext()

	const filters = useMemo(() => ({ idFerme, dateDebut: currentWeek }), [idFerme, currentWeek])

	// Les actions du tour de plaine travaillent sur les cultures mappées
	const selectedCrops = useMemo(() => inProgressCropsToApp(selectedRows), [selectedRows])

	const refreshData = useCallback((shouldUnselect) => {
		if (shouldUnselect) {
			resetSelectedRows()
		}
		refreshTable()
	}, [resetSelectedRows, refreshTable])

	const headers = useMemo(() => [
		{
			key: CROP_DATATABLE_KEYS.CROP,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviPepiniere.crops.table.headers.${CROP_DATATABLE_KEYS.CROP}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 120
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.CROP),
			checked: true,
			render: row => (
				<Typography className={classes.dataTableBoldText}>
					{row.assolement?.culture?.nom}
				</Typography>
			)
		},
		{
			key: CROP_DATATABLE_KEYS.STEP,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviPepiniere.crops.table.headers.${CROP_DATATABLE_KEYS.STEP}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 120
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.STEP),
			render: row => {
				const stepType = Object.keys(ETAPE_TYPE).find(key => key === (row.etape || ETAPE_CROISSANCE))
				return (
					<Typography className={classnames(classes.dataTableNormalText, { [classes.italicText]: ETAPE_TYPE[stepType] === ETAPE_TYPE.CROISSANCE }) }>
						{stepType && <FormattedMessage id={`enums.etape.${ETAPE_TYPE[stepType]}`} />}
					</Typography>
				)
			}
		},
		{
			key: CROP_DATATABLE_KEYS.SEEDLING_DATE,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviPepiniere.crops.table.headers.${CROP_DATATABLE_KEYS.SEEDLING_DATE}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 120
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.SEEDLING_DATE),
			render: row => (
				<Typography className={classes.dataTableNormalText}>
					{toI18N(formatDateToWeek(row.dateSemis))}
				</Typography>
			)
		},
		{
			key: CROP_DATATABLE_KEYS.PLANNED_BOARD,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviPepiniere.crops.table.headers.${CROP_DATATABLE_KEYS.PLANNED_BOARD}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 60
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.PLANNED_BOARD),
			render: row => (
				<Typography className={classes.dataTableNormalText}>
					{row.assolement?.planche?.nom}
				</Typography>
			)
		},
		{
			key: CROP_DATATABLE_KEYS.PLANNED_PREP_DATE,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviPepiniere.crops.table.headers.${CROP_DATATABLE_KEYS.PLANNED_PREP_DATE}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 120
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.PLANNED_PREP_DATE),
			render: row => {
				const endDate = moment(row.finRecolte, DateFormat.YYYY_MM_DD)
				const prepDate = moment(row.debutPreparation, DateFormat.YYYY_MM_DD)
				const showPrepWaring = prepDate.isSameOrBefore(endDate)
				return (
					<div className={classes.cell}>
						{showPrepWaring && <WarningRoundedIcon />}
						<Typography className={classnames(classes.dataTableNormalText, { [classes.datatableWarningText]: showPrepWaring })}>
							{toI18N(formatDateToWeek(row.debutPreparation))}
						</Typography>
					</div>
				)
			}
		},
		{
			key: CROP_DATATABLE_KEYS.PLANNED_IMPLANT_DATE,
			name: <Typography className={classes.dataTableHeader}><FormattedMessage id={`tourPlaine.suiviPepiniere.crops.table.headers.${CROP_DATATABLE_KEYS.PLANNED_IMPLANT_DATE}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 120
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.PLANNED_IMPLANT_DATE),
			render: row => {
				const prepDate = moment(row.debutPreparation, DateFormat.YYYY_MM_DD)
				const implantDate = moment(row.debutImplantation, DateFormat.YYYY_MM_DD)
				const showPlannedImplant = implantDate.isSameOrBefore(prepDate)
				return (
					<div className={classes.cell}>
						{showPlannedImplant && <WarningRoundedIcon />}
						<Typography className={classnames(classes.dataTableNormalText, { [classes.datatableWarningText]: showPlannedImplant })}>
							{toI18N(formatDateToWeek(row.debutImplantation))}
						</Typography>
					</div>
				)
			}
		},
		{
			key: CROP_DATATABLE_KEYS.CURRENT_HARVEST,
			name: <Typography className={classes.dataTableHeaderNext}><FormattedMessage id={`tourPlaine.suiviPepiniere.crops.table.headers.${CROP_DATATABLE_KEYS.CURRENT_HARVEST}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 120
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.CURRENT_HARVEST),
			render: row => (
				<Typography className={classes.dataTableBoardNext}>
					{row.assolementEnCoursOuSuivant?.culture?.nom ?? EMPTY_PLACEHOLDER}
				</Typography>
			)
		},
		{
			key: CROP_DATATABLE_KEYS.END_HARVEST,
			name: <Typography className={classes.dataTableHeaderNext}><FormattedMessage id={`tourPlaine.suiviPepiniere.crops.table.headers.${CROP_DATATABLE_KEYS.END_HARVEST}`} /></Typography>,
			tableHeaderColumnProps: {
				width: 120
			},
			sortable: SORTABLE_KEYS.includes(CROP_DATATABLE_KEYS.END_HARVEST),
			render: row => (
				<Typography className={classes.dataTableBoardNext}>
					{toI18N(formatDateToWeek(row.finRecolte))}
				</Typography>
			)
		}], [classes])

	const cropActions = getActions([
			ACTIONS_KEY.DETAIL_CROP,
			ACTIONS_KEY.MOVE_BOARD_STEP,
			ACTIONS_KEY.MOVE_SEEDLING_STEP,
			ACTIONS_KEY.ADD_COMMENT,
			ACTIONS_KEY.DELETE_CROP
		],
		selectedCrops,
		TOUR_PLAINE_TABS.SUIVI_PEPINIERE,
		refreshData)

	const nextCropActions = getActions([
			ACTIONS_KEY.DETAIL_CROP_CURRENT_OR_NEXT,
			ACTIONS_KEY.ADD_COMMENT_CURRENT_OR_NEXT
		],
		selectedCrops,
		TOUR_PLAINE_TABS.SUIVI_PEPINIERE,
		refreshData)

	return (
		<div className={classes.root}>
			<Grid item container className={classes.header}>
				<Title actionBlock={SP_BLOCK_ACTION_TYPE.CROPS} />
				<Grid item container direction="row" justify="flex-start" alignItems="center">
					<Grid item>
						<Typography className={classes.descriptionText}>
							<FormattedMessage id="tourPlaine.suiviPepiniere.crops.description" />
						</Typography>
					</Grid>
					{currentCampagneInProgress && <Grid item container xs direction="row" justify="flex-end" alignItems="flex-end" className={classes.actionContainer}>
						<Button
							type="secondary"
							startIcon={<AddCommentIcon />}
							onClick={() => openReminderForm(`${formatMessage({ id: 'tourPlaine.tabBar.suiviPepiniere' })} / ${formatMessage({ id: 'tourPlaine.suiviPepiniere.actionTab.crops' })}`)}
						>
							<FormattedMessage id="tourPlaine.actions.addReminder" />
						</Button>
						<ButtonWithMenu
							type="primary"
							startIcon={<MoreHorizIcon />}
							items={[
								{
									label: <FormattedMessage id="tourPlaine.actions.cultureCrop.suiviPepiniere" />,
									isTitle: true
								},
								...cropActions,
								{
									isDivider: true
								},
								{
									label: <FormattedMessage id="tourPlaine.actions.nextCrop.suiviPepiniere" />,
									isTitle: true
								},
								...nextCropActions
							]}
							disabled={!selectedRows.length}
						>
							<FormattedMessage id="tourPlaine.actions.others" />
						</ButtonWithMenu>
					</Grid>}
				</Grid>
			</Grid>
			<Grid item container className={classes.dataTableContainer}>
				<SpringDataTableWithLoader
					nom={SP_CROPS_TABLE_NAME}
					apiUrl={IN_NURSERY_CROPS_URI}
					filters={filters}
					pageSizes={[25, 50, 100]}
					defaultPageSize={25}
					tableProps={{
						stickyHeader: true,
						className: classes.dataTable
					}}
					noResultFragment={<NoResultPage emptyStateMessageId="tourPlaine.suiviParBloc.crops.table.empty" />}
					selectable={currentCampagneInProgress}
					displaySelectAll={currentCampagneInProgress}
					headers={headers}
					loaderStyle={{ width: '100%' }}
					messageStyle={{ verticalAlign: 'top', paddingTop: 72 }}
					hideContentOnLoad={false}
					getRowKey={row => row.assolement.id}
				/>
			</Grid>
		</div>
	)
}

CropsContent.propTypes = {
	classes: PropTypes.object,
	openReminderForm: PropTypes.func,
	getActions: PropTypes.func,
	idFerme: PropTypes.string,
	selectedRows: PropTypes.array,
	refreshTable: PropTypes.func,
	resetSelectedRows: PropTypes.func
}

const mapStateToProps = (state) => ({
	idFerme: getPreferences(state).FERME_ID,
	selectedRows: (state.dataTable[SP_CROPS_TABLE_NAME] || {}).selectedRows || []
})

const actions = {
	refreshTable: () => dataTableActions.refresh(SP_CROPS_TABLE_NAME),
	resetSelectedRows: () => dataTableActions.selectRows(SP_CROPS_TABLE_NAME, [])
}

export default compose(
	injectReminderForm,
	injectActionsForm,
	connect(mapStateToProps, actions),
	withStyles(contentStyles)
)
(CropsContent)
